import {Injectable} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PlotOptions} from 'highcharts';

@Injectable()
export class ChartService {

  // ── Shared modern theme pieces ──────────────────────────────────────
  private static readonly fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  private static baseTooltip(): any {
    return {
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderWidth: 0,
      borderRadius: 12,
      shadow: {
        offsetX: 0,
        offsetY: 4,
        width: 16,
        color: 'rgba(0,0,0,0.08)'
      },
      style: {
        fontSize: '13px',
        fontFamily: ChartService.fontFamily,
        color: '#1e293b'
      },
      padding: 12,
      useHTML: true
    };
  }

  private static baseLegend(): any {
    return {
      itemStyle: {
        color: '#64748b',
        fontSize: '13px',
        fontWeight: '500',
        fontFamily: ChartService.fontFamily
      },
      itemHoverStyle: {
        color: '#1e293b'
      },
      symbolRadius: 4
    };
  }

  private static baseChart(type: string, extra: any = {}): any {
    return {
      type,
      style: { fontFamily: ChartService.fontFamily },
      backgroundColor: 'transparent',
      spacing: [10, 10, 10, 10],
      animation: {
        duration: 800,
        easing: 'easeOutQuart'
      },
      ...extra
    };
  }

  private static modernXAxis(categories: any[], extra: any = {}): any {
    return {
      categories,
      title: { text: null },
      labels: {
        style: {
          color: '#94a3b8',
          fontSize: '12px',
          fontFamily: ChartService.fontFamily
        }
      },
      lineColor: '#e2e8f0',
      tickColor: 'transparent',
      ...extra
    };
  }

  private static modernYAxis(extra: any = {}): any {
    return {
      min: 0,
      title: { text: undefined },
      labels: {
        style: {
          color: '#94a3b8',
          fontSize: '12px',
          fontFamily: ChartService.fontFamily
        },
        overflow: 'justify'
      },
      gridLineColor: '#f1f5f9',
      gridLineDashStyle: 'Dash',
      ...extra
    };
  }

  private static showLoadingOnEmpty(): any {
    return {
      load: function () {
        const sumCount = this.series[0].data.reduce((sum, current) => sum + (current.y || current.options?.y || 0), 0);
        if (!sumCount) {
          this.showLoading();
        }
      }
    };
  }

  private static showLoadingOnEmptyLength(): any {
    return {
      load: function () {
        if (!this.xAxis[0].series[0].data.length) {
          this.showLoading();
        }
      }
    };
  }

  // ── Modern pie / donut shared config ────────────────────────────────
  private static modernPiePlotOptions(borderWidth = 0): any {
    return {
      pie: {
        borderWidth,
        borderColor: '#ffffff',
        innerSize: '72%',
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: { enabled: false },
        showInLegend: true,
        shadow: false,
        states: {
          hover: {
            brightness: 0.05,
            halo: {
              size: 8,
              opacity: 0.15
            }
          },
          inactive: {
            opacity: 0.4
          }
        },
        point: {
          events: {
            mouseOver: function () {
              this.graphic?.animate({ r: this.shapeArgs.r + 4 }, { duration: 200 });
            },
            mouseOut: function () {
              this.graphic?.animate({ r: this.shapeArgs.r }, { duration: 200 });
            }
          }
        }
      }
    };
  }

  private static modernPieTooltip(): any {
    return {
      ...ChartService.baseTooltip(),
      pointFormat: '<span style="font-size:22px;font-weight:700;color:{point.color}">{point.percentage:.1f}%</span>' +
        '<br/><span style="font-size:12px;color:#94a3b8">{point.y} users</span>'
    };
  }

  // ── Modern bar / column shared config ──────────────────────────────
  private static modernBarPlotOptions(isBar = true): any {
    const key = isBar ? 'bar' : 'column';
    return {
      [key]: {
        borderRadius: 6,
        borderWidth: 0,
        groupPadding: 0.15,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: '600',
            fontFamily: ChartService.fontFamily,
            color: '#475569',
            textOutline: 'none'
          }
        },
        states: {
          hover: {
            brightness: -0.06
          },
          inactive: {
            opacity: 0.5
          }
        }
      },
      series: {
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        }
      }
    };
  }

  private static modernBarTooltip(suffix = ''): any {
    return {
      ...ChartService.baseTooltip(),
      pointFormat: `<span style="font-size:20px;font-weight:700;color:{point.color}">{point.y}${suffix}</span>`
    };
  }

  // ── Modern loading ──────────────────────────────────────────────────
  private static modernLoading(): any {
    return {
      hideDuration: 400,
      showDuration: 400,
      style: {
        position: 'absolute',
        backgroundColor: 'rgba(255,255,255,0.85)',
        textAlign: 'center',
        fontSize: '14px',
        color: '#94a3b8'
      }
    };
  }

  // ── Gradient helper for column series ──────────────────────────────
  private static columnGradient(hex: string): any {
    return {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, hex],
        [1, ChartService.adjustBrightness(hex, -25)]
      ]
    };
  }

  private static adjustBrightness(hex: string, amount: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, ((num >> 16) & 0xff) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
    const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  // ═══════════════════════════════════════════════════════════════════
  // BAR CHARTS
  // ═══════════════════════════════════════════════════════════════════

  public static getHashtagChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Count',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#286764'], [1, '#34a19b']]
      }
    });

    if (data.user.tags !== null) {
      for (const i in data.user.tags.ranking) {
        const d = data.user.tags.ranking[i];
        categories.push(d.tag);
        series[0].data.push(d.num);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar'),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis({ title: { text: 'Count of uses', align: 'high', style: { color: '#94a3b8', fontSize: '11px' } } }),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip(),
      credits: { enabled: false },
      series: series
    });
  }

  public static getInterestChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#FB9985'], [1, '#ff6b6b']]
      }
    });

    if (data.user.audience.interests !== null) {
      for (const i in data.user.audience.interests) {
        const d = data.user.audience.interests[i];
        categories.push(i);
        series[0].data.push(d);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar'),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip('%'),
      credits: { enabled: false },
      series: series
    });
  }

  public static getLanguageChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#FB9985'], [1, '#ff6b6b']]
      }
    });

    if (data.user.audience.langGenderFollow.langs !== null) {
      for (const i in data.user.audience.langGenderFollow.langs) {
        const d = data.user.audience.langGenderFollow.langs[i];
        categories.push(i);
        series[0].data.push(d);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar'),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip('%'),
      credits: { enabled: false },
      series: series
    });
  }

  public static getLanguageAudienceChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#957AED'], [1, '#b794f4']]
      }
    });

    if (data.lang !== null) {
      for (const i in data.lang.buckets) {
        const d = data.lang.buckets[i];
        categories.push(d.key.toUpperCase());
        series[0].data.push(Math.round((d.unique.value / data.lang_total.unique.value) * 10000) / 100);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip('%'),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // PIE / DONUT CHARTS
  // ═══════════════════════════════════════════════════════════════════

  public static getGenderChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Gender',
      data: []
    });
    const dataArray = {
      male: 0,
      female: 0
    };

    if (data.user.audience.langGenderFollow !== null) {
      dataArray.male = data.user.audience.langGenderFollow.genders[1];
      dataArray.female = data.user.audience.langGenderFollow.genders[2];
    }

    if (dataArray !== null) {
      series[0].data.push({
        y: dataArray.male,
        name: 'Male',
        color: '#0AAEFF',
        dataLabels: { enabled: false }
      });
      series[0].data.push({
        y: dataArray.female,
        name: 'Female',
        color: '#FF72B5',
        dataLabels: { enabled: false }
      });
    }

    return new Chart({
      chart: ChartService.baseChart('pie', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '', align: 'center', verticalAlign: 'middle' },
      credits: { enabled: false },
      legend: { enabled: false, ...ChartService.baseLegend() },
      tooltip: ChartService.modernPieTooltip(),
      series: series,
      plotOptions: ChartService.modernPiePlotOptions()
    });
  }

  public static getGenderAudienceChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Percent of users',
      data: []
    });
    const dataArray = {
      male: 0,
      female: 0,
      undefined: 0
    };

    if (data.gender !== undefined) {
       const male = data.gender.buckets.find(x => x.key === 2);
       const female = data.gender.buckets.find(x => x.key === 1);
       const total = data.gender_total.unique.value > 0 ? data.gender_total.unique.value : 1;
       dataArray.male = Math.round(((male !== undefined ? male.unique.value : 0) / total) * 10000) / 100;
       dataArray.female = Math.round(((female !== undefined ? female.unique.value : 0) / total) * 10000) / 100;
    }

    if (dataArray !== null) {
      series[0].data.push({
        y: dataArray.male,
        name: 'Male',
        color: '#0AAEFF',
        dataLabels: { enabled: false }
      });
      series[0].data.push({
        y: dataArray.female,
        name: 'Female',
        color: '#FF72B5',
        dataLabels: { enabled: false }
      });
    }

    return new Chart({
      chart: ChartService.baseChart('pie', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '', align: 'center', verticalAlign: 'middle' },
      credits: { enabled: false },
      series: series,
      legend: { enabled: false, ...ChartService.baseLegend() },
      tooltip: ChartService.modernPieTooltip(),
      plotOptions: ChartService.modernPiePlotOptions()
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // COLUMN CHARTS
  // ═══════════════════════════════════════════════════════════════════

  public static getAgeAudienceChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: ChartService.columnGradient('#25CCD2')
    });

    if (data.age !== null) {
      for (const i in data.age.buckets) {
        const d = data.age.buckets[i];
        categories.push(d.key);
        series[0].data.push(Math.round((d.unique.value / data.age_total.value) * 10000) / 100);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column', { height: 260, events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip('%'),
      legend: { enabled: false },
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getLikeByDayChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'No. of likes',
      data: [],
      color: ChartService.columnGradient('#FB9985')
    });

    if (data !== null) {
      for (const i in data.buckets) {
        const d = data.buckets[i];
        const a = new Date(d.key);
        categories.push(a.getDate() + '-' + (a.getMonth() + 1) + '-' + a.getFullYear());
        series[0].data.push(d.like_sum.value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column'),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip(),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series
    });
  }

  public static getCommentByDayChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'No. of comments',
      data: [],
      color: ChartService.columnGradient('#FB9985')
    });

    if (data !== null) {
      for (const i in data.buckets) {
        const d = data.buckets[i];
        const a = new Date(d.key);
        categories.push(a.getDate() + '-' + (a.getMonth() + 1) + '-' + a.getFullYear());
        series[0].data.push(d.comment_sum.value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column'),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip(),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series
    });
  }

  public static getMediaByDayChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'No. of posts',
      data: [],
      color: ChartService.columnGradient('#FB9985')
    });

    if (data !== null) {
      for (const i in data.buckets) {
        const d = data.buckets[i];
        const a = new Date(d.key);
        categories.push(a.getDate() + '-' + (a.getMonth() + 1) + '-' + a.getFullYear());
        series[0].data.push(d.doc_count);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column'),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip(),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series
    });
  }

  public static getMediaByHourSumChart(data: any): Chart {
    const categories = [];
    const series = [];
    const offsetTimezone = new Date().getTimezoneOffset() / 60;

    series.push({
      name: 'No. of posts',
      data: [],
      color: ChartService.columnGradient('#FFC212')
    });

    if (data !== null) {
      const cache = [];
      for (const i in data.buckets) {
        const d = data.buckets[i];

        cache.push({
            cat: ((d.key - offsetTimezone + 24) % 24),
            value: d.doc_count
        });
      }

      cache.sort((a, b) => {
          if (a.cat < b.cat) { return -1; }
          if (a.cat > b.cat) { return 1; }
          return 0;
      });

      for (const i in cache) {
          categories.push(cache[i].cat);
          series[0].data.push(cache[i].value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip(),
      credits: { enabled: false },
      legend: ChartService.baseLegend(),
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getMediaByHourMetricChart(data: any, metric: string): Chart {
    const categories = [];
    const series = [];
    const offsetTimezone = new Date().getTimezoneOffset() / 60;

    let name = 'No. of posts';
    let color = '#0AAEFF';
    if (metric === 'like') {
      name = 'No. of likes';
      color = '#25CCD2';
    } else if (metric === 'comment') {
      name = 'No. of comments';
      color = '#2EC973';
    } else if (metric === 'reach') {
      name = 'Reach';
      color = '#FFC212';
    }

    series.push({
      name: name,
      data: [],
      color: ChartService.columnGradient(color)
    });

    if (data !== null) {
      const cache = [];
      for (const i in data.hour_sum.buckets) {
        const d = data.hour_sum.buckets[i];
        let value = d.doc_count;
        if (metric === 'like') {
          value = d.like_sum.value;
        } else if (metric === 'comment') {
          value = d.comment_sum.value;
        } else if (metric === 'reach') {
          value = d.reach_sum.value;
        }

        cache.push({
          cat: ((d.key - offsetTimezone + 24) % 24),
          value: value
        });
      }

      cache.sort((a, b) => {
        if (a.cat < b.cat) { return -1; }
        if (a.cat > b.cat) { return 1; }
        return 0;
      });

      for (const i in cache) {
        categories.push(cache[i].cat);
        series[0].data.push(cache[i].value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip(),
      credits: { enabled: false },
      legend: ChartService.baseLegend(),
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getMediaByWeekDayChart(data: any): Chart {
    const categories = [];
    const series = [];
    const weekDay = {1: 'Mon.', 2: 'Tue.', 3: 'Wed.', 4: 'Thu.', 5: 'Fri.', 6: 'Sat.', 7: 'Sun.'};
    series.push({
      name: 'No. of posts',
      data: [],
      color: ChartService.columnGradient('#2EC973')
    });

    if (data !== null) {
      for (const i in data.buckets) {
        const d = data.buckets[i];
        categories.push(weekDay[d.key]);
        series[0].data.push(d.doc_count);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip(),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getMediaByWeekDayMetricChart(data: any, metric: string): Chart {
    const categories = [];
    const series = [];
    const weekDay = {1: 'Mon.', 2: 'Tue.', 3: 'Wed.', 4: 'Thu.', 5: 'Fri.', 6: 'Sat.', 7: 'Sun.'};

    let name = 'No. of posts';
    let color = '#0AAEFF';
    if (metric === 'like') {
      name = 'No. of likes';
      color = '#25CCD2';
    } else if (metric === 'comment') {
      name = 'No. of comments';
      color = '#2EC973';
    } else if (metric === 'reach') {
      name = 'Reach';
      color = '#FFC212';
    }

    series.push({
      name: name,
      data: [],
      color: ChartService.columnGradient(color)
    });

    if (data !== null) {
      for (const i in data.week_day.buckets) {
        const d = data.week_day.buckets[i];
        categories.push(weekDay[d.key]);
        let value = d.doc_count;
        if (metric === 'like') {
          value = d.like_sum.value;
        } else if (metric === 'comment') {
          value = d.comment_sum.value;
        } else if (metric === 'reach') {
          value = d.reach_sum.value;
        }
        series[0].data.push(value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip(),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getEngageByWeekDayChart(data: any): Chart {
    const categories = [];
    const series = [];
    const weekDay = {1: 'Mon.', 2: 'Tue.', 3: 'Wed.', 4: 'Thu.', 5: 'Fri.', 6: 'Sat.', 7: 'Sun.'};

    series.push({
      name: 'Engage %',
      data: [],
      color: ChartService.columnGradient('#3c70f8')
    });

    if (data !== null) {
      for (const i in data.engage_week_day) {
        const d = data.engage_week_day[i];
        categories.push(weekDay[d.key]);
        const value = d.value;

        series[0].data.push(value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('column', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(false),
      tooltip: ChartService.modernBarTooltip('%'),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getMediaHashtagChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'No. of cases',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#0AAEFF'], [1, '#38bdf8']]
      }
    });

    if (data !== null) {
      for (const i in data.buckets) {
        const d = data.buckets[i];
        categories.push('#' + d.key);
        series[0].data.push(d.doc_count);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar', { events: ChartService.showLoadingOnEmptyLength() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip(),
      credits: { enabled: false },
      series: series,
      legend: ChartService.baseLegend(),
      loading: ChartService.modernLoading()
    });
  }

  public static getMediaTagChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'No. of cases',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#25CCD2'], [1, '#5eead4']]
      }
    });

    if (data !== null) {
      for (const i in data.buckets) {
        const d = data.buckets[i];
        categories.push(d.key);
        series[0].data.push(d.doc_count);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar', { events: ChartService.showLoadingOnEmptyLength() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip(),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getInterestAudienceChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#0AAEFF'], [1, '#38bdf8']]
      }
    });

    if (data !== null) {
      for (let i in data.slice(0, 6)) {
        let d = data[i];
        d.key = d.key.charAt(0).toUpperCase() + d.key.slice(1);
        categories.push(d.key);
        series[0].data.push(Math.round(d.percent * 100) / 100);

      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: {
        ...ChartService.modernBarPlotOptions(true),
        bar: {
          ...ChartService.modernBarPlotOptions(true).bar,
          dataLabels: {
            ...ChartService.modernBarPlotOptions(true).bar.dataLabels,
            inside: false
          }
        }
      },
      tooltip: ChartService.modernBarTooltip('%'),
      credits: { enabled: false },
      legend: ChartService.baseLegend(),
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // SPLINE / AREA CHARTS
  // ═══════════════════════════════════════════════════════════════════

  public static getEngageByDayChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Engage %',
      data: [],
      color: '#0AAEFF',
      type: 'area'
    });

    if (data !== null) {
      for (const i in data.engage) {
        const d = data.engage[i];
        if (d.value === 0) {
          continue;
        }
        d.value = Math.round(d.value * 100) / 100;
        const a = new Date(d.day * 1000);
        categories.push(a.getDate() + '-' + (a.getMonth() + 1) + '-' + a.getFullYear());
        series[0].data.push(d.value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('spline', { events: ChartService.showLoadingOnEmptyLength() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: {
        area: {
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(10,174,255,0.18)'],
              [0.7, 'rgba(10,174,255,0.04)'],
              [1, 'rgba(10,174,255,0)']
            ]
          },
          marker: {
            radius: 4,
            fillColor: '#FFFFFF',
            lineWidth: 2,
            lineColor: '#0AAEFF',
            symbol: 'circle',
            states: {
              hover: {
                radius: 7,
                lineWidth: 2
              }
            }
          },
          lineWidth: 2.5,
          states: {
            hover: {
              lineWidth: 3
            }
          },
          threshold: null
        },
        series: {
          animation: {
            duration: 1200,
            easing: 'easeOutQuart'
          }
        }
      },
      tooltip: {
        ...ChartService.baseTooltip(),
        pointFormat: '<span style="font-size:20px;font-weight:700;color:#0AAEFF">{point.y}%</span>'
      },
      credits: { enabled: false },
      legend: ChartService.baseLegend(),
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getChartDate(data: any, key, metric): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'No. of posts',
      data: [],
      color: '#0AAEFF',
      type: 'area'
    });

    if (data !== null) {
      for (const i in data[key].buckets) {
        const d = data[key].buckets[i];
        let v = 0;
        if (metric === 'doc_count') {
          v = d['doc_count'];
        } else {
          v = d[metric]['value'];
        }
        const a = new Date(d.key);
        categories.push(a.getDate() + '-' + (a.getMonth() + 1) + '-' + a.getFullYear());
        series[0].data.push(v);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('spline', { events: ChartService.showLoadingOnEmptyLength() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: {
        area: {
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(10,174,255,0.18)'],
              [0.7, 'rgba(10,174,255,0.04)'],
              [1, 'rgba(10,174,255,0)']
            ]
          },
          marker: {
            radius: 4,
            fillColor: '#FFFFFF',
            lineWidth: 2,
            lineColor: '#0AAEFF',
            symbol: 'circle',
            states: {
              hover: {
                radius: 7,
                lineWidth: 2
              }
            }
          },
          lineWidth: 2.5,
          states: {
            hover: {
              lineWidth: 3
            }
          },
          threshold: null
        },
        series: {
          animation: {
            duration: 1200,
            easing: 'easeOutQuart'
          }
        }
      },
      tooltip: {
        ...ChartService.baseTooltip(),
        pointFormat: '<span style="font-size:20px;font-weight:700;color:#0AAEFF">{point.y}</span>'
      },
      credits: { enabled: false },
      legend: ChartService.baseLegend(),
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // AREASPLINE CHART
  // ═══════════════════════════════════════════════════════════════════

  public static getMediaCommentFromPostDateChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'No. of followers comments',
      data: [],
      color: '#FB9985',
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(251,153,133,0.2)'],
          [1, 'rgba(251,153,133,0)']
        ]
      },
      marker: {
        radius: 3,
        fillColor: '#FFFFFF',
        lineWidth: 2,
        lineColor: '#FB9985',
        symbol: 'circle'
      },
      lineWidth: 2.5
    }, {
      name: 'No. of guests comments',
      data: [],
      color: '#646C79',
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(100,108,121,0.15)'],
          [1, 'rgba(100,108,121,0)']
        ]
      },
      marker: {
        radius: 3,
        fillColor: '#FFFFFF',
        lineWidth: 2,
        lineColor: '#646C79',
        symbol: 'circle'
      },
      lineWidth: 2.5
    });

    if (data !== null) {
      for (const i in data) {
        const d = data[i];
        categories.push(d.key);
        series[0].data.push(d.follower.value);
        series[1].data.push(d.guest.value);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('areaspline'),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: {
        areaspline: {
          stacking: 'overlap',
          states: {
            hover: { lineWidth: 3 },
            inactive: { opacity: 0.3 }
          }
        },
        series: {
          animation: {
            duration: 1200,
            easing: 'easeOutQuart'
          }
        }
      },
      tooltip: {
        ...ChartService.baseTooltip(),
        shared: true,
        pointFormat: '<span style="color:{point.color}">\u25cf</span> {series.name}: <b>{point.y}</b><br/>'
      },
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // LOCATION CHARTS
  // ═══════════════════════════════════════════════════════════════════

  public static getLocationCountryForMap(data: any): any {
    const out: any = {};
    if (data !== null) {
      for (let i in data) {
        let d = data[i];
        if (d['key'] == 'xx' || d['key'] == 'un' || d['key'] == '**') {
          continue;
        }
        out[d['key']] = {
          value: d['count']
        };
      }
    }
    return out;
  }

  public static getLocationCityAudienceChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#2EC973'], [1, '#6ee7b7']]
      }
    });

    if (data !== null) {
      for (let i in data) {
        if (parseInt(i) > 10) { continue; }
        let d = data[i];
        d.key = d.key.charAt(0).toUpperCase() + d.key.slice(1);
        categories.push(d.key);
        series[0].data.push(Math.round(d.percent * 100) / 100);
      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip('%'),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  public static getLocationCountryAudienceChart(data: any): Chart {
    const categories = [];
    const series = [];
    series.push({
      name: 'Percent of users',
      data: [],
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        stops: [[0, '#FFC212'], [1, '#fde68a']]
      }
    });

    if (data !== null) {
      for (let i in data) {
        if (parseInt(i) > 10) { continue; }
        const d = data[i];
        categories.push(d.key.toUpperCase());
        series[0].data.push(Math.round(d.percent * 100) / 100);

      }
    }

    return new Chart({
      chart: ChartService.baseChart('bar', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '' },
      xAxis: ChartService.modernXAxis(categories),
      yAxis: ChartService.modernYAxis(),
      plotOptions: ChartService.modernBarPlotOptions(true),
      tooltip: ChartService.modernBarTooltip('%'),
      legend: ChartService.baseLegend(),
      credits: { enabled: false },
      series: series,
      loading: ChartService.modernLoading()
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // CONDITION LEVEL HELPERS
  // ═══════════════════════════════════════════════════════════════════

  public static conditionLevel(percent: number) {
    if (percent > 80) {
      return {
        text: 'Excellent',
        color: '#2ec973',
        bgcolor: 'rgba(46,201,115,0.2)'
      };
    } else if (percent > 70) {
      return {
        text: 'Very good',
        color: '#0ac28b',
        bgcolor: 'rgba(46,201,115,0.1)'

      };
    } else if (percent > 55) {
      return {
        text: 'Good',
        color: '#00e6b0',
        bgcolor: 'rgba(46,201,115,0.1)'

      };
    } else if (percent > 40) {
      return {
        text: 'Medium',
        color: '#FF7D51',
        bgcolor: 'rgba(255,125,81,0.2)'
      };
    } else if (percent > 25) {
      return {
        text: 'Bad',
        color: '#FB9985',
        bgcolor: 'rgba(255,125,81,0.3)'
      };
    } else {
      return {
        text: 'Very bad',
        color: '#FF5656',
        bgcolor: 'rgba(255,86,86,0.2)'
      };
    }
  }
  public static conditionLevelBlue(percent: number) {
    if (percent > 80) {
      return {
        'text': 'Excellent',
        'dotcolor': '#25CCD2',
        'bgcolor': 'rgba(37,204,210,0.2)'
      };
    } else if (percent > 70) {
      return {
        'text': 'Very good',
        'dotcolor': '#25CCD2',
        'bgcolor': 'rgba(37,204,210,0.2)'

      };
    } else if (percent > 55) {
      return {
        'text': 'Good',
        'dotcolor': '#25CCD2',
        'bgcolor': 'rgba(37,204,210,0.2)'

      };
    } else if (percent > 40) {
      return {
        'text': 'Medium',
        'dotcolor': '#FF5656',
        'bgcolor': 'rgba(255,86,86,0.2)'
      };
    } else if (percent > 25) {
      return {
        'text': 'Bad',
        'dotcolor': '#FF5656',
        'bgcolor': 'rgba(255,86,86,0.2)'
      };
    } else {
      return {
        text: 'Very bad',
        'dotcolor': '#FF5656',
        'bgcolor': 'rgba(255,86,86,0.2)'
      };
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // MORE PIE / DONUT CHARTS
  // ═══════════════════════════════════════════════════════════════════

  public static getQualityDistributionChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Percent of users',
      data: []
    });

    const dataArray = {
      verygood: 0,
      good: 0,
      medium: 0,
      bad: 0,
      verybad: 0
    };

    if (data.quality_distribution !== null) {
      dataArray.verygood = Math.round(data.quality_distribution.verygood.percent * 100) / 100;
      dataArray.good = Math.round(data.quality_distribution.good.percent * 100) / 100;
      dataArray.medium = Math.round(data.quality_distribution.medium.percent * 100) / 100;
      dataArray.bad = Math.round(data.quality_distribution.bad.percent * 100) / 100;
      dataArray.verybad = Math.round(data.quality_distribution.verybad.percent * 100) / 100;
    }

    if (dataArray !== null) {
      series[0].data.push({
        y: dataArray.verygood,
        name: 'Very good',
        color: '#2EC973',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 300 followings'
      });
      series[0].data.push({
        y: dataArray.good,
        name: 'Good',
        color: '#25CCD2',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 500 followings'
      });
      series[0].data.push({
        y: dataArray.medium,
        name: 'Medium',
        color: '#FFC212',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 1500 followings'
      });
      series[0].data.push({
        y: dataArray.bad,
        name: 'Bad',
        color: '#FF7D51',
        dataLabels: { enabled: false },
        info: 'Accounts that have no more than 2500 followings'
      });
      series[0].data.push({
        y: dataArray.verybad,
        name: 'Very bad',
        color: '#FF5656',
        dataLabels: { enabled: false },
        info: 'Accounts that have more than 2500 followings'
      });
    }

    return new Chart({
      chart: ChartService.baseChart('pie', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '', align: 'center', verticalAlign: 'middle' },
      legend: { enabled: false, ...ChartService.baseLegend(), itemMarginBottom: 20, margin: 30 },
      credits: { enabled: false },
      series: series,
      tooltip: ChartService.modernPieTooltip(),
      plotOptions: ChartService.modernPiePlotOptions()
    });
  }

  public static getAudienceTypeChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Percent of users',
      data: []
    });

    const dataArray = {
      influencers: 0,
      massFollowers: 0,
      realUsers: 0,
      ghostUsers: 0
    };

    if (data.followers !== null) {
      dataArray.influencers = Math.round(data.followers.influencer.percent * 100) / 100;
      dataArray.massFollowers = Math.round(data.followers.mass_followers.percent * 100) / 100;
      dataArray.realUsers = Math.round(data.followers.real_people.percent * 100) / 100;
      dataArray.ghostUsers = Math.round(data.followers.ghost_followers.percent * 100) / 100;
    }
    series[0].data.push({
      y: dataArray.realUsers,
      name: 'Real users',
      color: '#2EC973',
      dataLabels: { enabled: false },
      info: 'Accounts that have less than 1500 followings'
    });
    series[0].data.push({
      y: dataArray.ghostUsers,
      name: 'Shadow accounts',
      color: '#D3D8DE',
      dataLabels: { enabled: false },
      info: 'Limited number of public activity'
    });
    series[0].data.push({
      y: dataArray.influencers,
      name: 'Influencers',
      color: '#25CCD2',
      dataLabels: { enabled: false },
      info: 'Accounts that have more than 5000 followers'
    });
    series[0].data.push({
      y: dataArray.massFollowers,
      name: 'Mass followers',
      color: '#99A4B2',
      dataLabels: { enabled: false },
      info: 'Accounts that have more than 1500 followings'
    });

    return new Chart({
      chart: ChartService.baseChart('pie', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '', align: 'center', verticalAlign: 'middle' },
      credits: { enabled: false },
      legend: { enabled: false, ...ChartService.baseLegend(), itemMarginBottom: 20, margin: 30 },
      series: series,
      tooltip: ChartService.modernPieTooltip(),
      plotOptions: ChartService.modernPiePlotOptions()
    });
  }

    public static getIsChart(data: any, type: string): Chart {
        const series = [];
        series.push({
            name: 'Percent of users',
            data: []
        });

        const dataArray = {
            is: 0,
            is_not: 0
        };
        let names = {
            is: '',
            is_not: ''
        };
        if (type === 'private') {
          names.is = 'Private';
          names.is_not = 'Regular';
        }
        if (type === 'business') {
          names.is = 'Business';
          names.is_not = 'Standard';
        }

        if (data !== null) {
            dataArray.is = Math.round(data.is.percent * 100) / 100;
            dataArray.is_not = Math.round(data.is_not.percent * 100) / 100;
        }
        series[0].data.push({
            y: dataArray.is,
            name: names.is,
            color: '#25CCD2',
            dataLabels: { enabled: false }
        });
        series[0].data.push({
            y: dataArray.is_not,
            name: names.is_not,
            color: '#99A4B2',
            dataLabels: { enabled: false }
        });

        return new Chart({
            chart: ChartService.baseChart('pie', { events: ChartService.showLoadingOnEmpty() }),
            title: { text: '', align: 'center', verticalAlign: 'middle' },
            credits: { enabled: false },
            legend: { enabled: false, ...ChartService.baseLegend() },
            series: series,
            tooltip: ChartService.modernPieTooltip(),
            plotOptions: ChartService.modernPiePlotOptions()
        });
    }

  public static getMediaTypeChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Percent of users',
      data: []
    });

    const dataArray = {
      true_media: 0,
      true_guest_media: 0,
      false_media: 0,
    };

    if (data.media_distribution !== null) {
      dataArray.true_media = data.media_distribution.true_media.value;
      dataArray.true_guest_media = data.media_distribution.true_guest_media.value;
      dataArray.false_media = data.media_distribution.false_media.value;
    }
    const sum = dataArray.true_media + dataArray.true_guest_media + dataArray.false_media;

    series[0].data.push({
      y: this.countPercent(dataArray.true_media, sum),
      name: 'True',
      color: '#0AAEFF',
      dataLabels: { enabled: false }
    });
    series[0].data.push({
      y: this.countPercent(dataArray.true_guest_media, sum),
      name: 'True guest',
      color: '#25CCD2',
      dataLabels: { enabled: false }
    });
    series[0].data.push({
      y: this.countPercent(dataArray.false_media, sum),
      name: 'Suspicious',
      color: '#D3D8DE',
      dataLabels: { enabled: false }
    });

    return new Chart({
      chart: ChartService.baseChart('pie', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '', align: 'center', verticalAlign: 'middle' },
      credits: { enabled: false },
      legend: { enabled: false, ...ChartService.baseLegend(), itemMarginBottom: 15, margin: 30 },
      series: series,
      tooltip: ChartService.modernPieTooltip(),
      plotOptions: ChartService.modernPiePlotOptions()
    });
  }

  public static getMediaTypeWithOutGuestChart(data: any): Chart {
    const series = [];
    series.push({
      name: 'Percent of users',
      data: []
    });

    const dataArray = {
      true_media: 0,
      true_guest_media: 0,
      false_media: 0,
    };

    if (data.media_distribution !== null) {
      dataArray.true_media = data.media_distribution.true_media.value;
      dataArray.true_guest_media = data.media_distribution.true_guest_media.value;
      dataArray.false_media = data.media_distribution.false_media.value;
    }

    const sum = dataArray.true_media + dataArray.true_guest_media + dataArray.false_media;

    series[0].data.push({
      y: this.countPercent(dataArray.true_media, sum),
      name: 'True',
      color: '#0AAEFF',
      dataLabels: { enabled: false }
    });
    series[0].data.push({
      y: this.countPercent(dataArray.false_media + dataArray.true_guest_media, sum),
      name: 'Suspicious',
      color: '#D3D8DE',
      dataLabels: { enabled: false }
    });

    return new Chart({
      chart: ChartService.baseChart('pie', { events: ChartService.showLoadingOnEmpty() }),
      title: { text: '', align: 'center', verticalAlign: 'middle' },
      credits: { enabled: false },
      legend: { enabled: false, ...ChartService.baseLegend(), itemMarginBottom: 15, margin: 30 },
      series: series,
      tooltip: ChartService.modernPieTooltip(),
      plotOptions: ChartService.modernPiePlotOptions()
    });
  }

  public static countPercent(value, total) {
    if (total === 0) {
      return 0;
    }
    return Math.round((value / total) * 10000) / 100;
  }
}